/**
 * Phase 1 · Voucher Presentation Gateway.
 *
 * Single decision point for READ-MODE voucher rendering. Every surface
 * (Guest Wallet, Voucher Types thumbnail, Voucher Builder preview)
 * calls this so architecture cannot drift — one voucher → one visible
 * composition everywhere.
 *
 * Decision matrix:
 *   canonical V2/V3 design healthy   → <VoucherDesignRenderer>
 *   otherwise (legacy V1 / null)     → <PremiumVoucherCard>
 *
 * "Healthy" is intentionally stricter than the raw `design_version >= 2`
 * check — a V2 payload with 0 elements or a non-positive aspect ratio
 * collapses the canvas, so it is downgraded to the legacy path.
 *
 * A per-instance error boundary isolates any exception thrown during
 * design rendering; commercial voucher visibility never depends on
 * design payload integrity.
 */
import React from 'react';
import PremiumVoucherCard from './VoucherCard';
import VoucherDesignRenderer from './VoucherDesignRenderer';

class VoucherErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err) {
    console.warn('[voucher-presentation] renderer crashed → legacy fallback:', err?.message);
  }
  render() { return this.state.hasError ? this.props.fallback : this.props.children; }
}

function designHealthy(d) {
  if (!d || typeof d !== 'object') return false;
  if ((d.design_version || 0) < 2) return true;  // legacy V1 is always safe
  const canvas = d.canvas || {};
  const ar = Number(canvas.aspect_ratio) || 0;
  return ar > 0 && Array.isArray(d.elements) && d.elements.length > 0;
}

export default function VoucherPresentation({
  voucher, design, outletName, outletTagline, outletInitials, outletLogo,
  state, validUntil, size = 'phone',
  // Phase 2B · reveal state — plumbed through both surfaces so a
  // "Reveal" tap in the wallet swaps the QR placeholder for the real
  // signed credential and reveals the plaintext code layer.
  revealed = false, qrPayload = null, revealedCode = null,
}) {
  const legacy = (
    <PremiumVoucherCard
      voucher={voucher}
      design={design}
      outletName={outletName}
      outletInitials={outletInitials}
      outletLogo={outletLogo}
      state={state}
      validUntil={validUntil}
      size={size}
      revealed={revealed}
      qrPayload={qrPayload}
      revealedCode={revealedCode}
    />
  );
  if (!designHealthy(design) || (design.design_version || 0) < 2) return legacy;
  return (
    <VoucherErrorBoundary fallback={legacy}>
      <VoucherDesignRenderer
        design={design}
        voucher={voucher}
        outlet={{ name: outletName, tagline: outletTagline || '',
                  logo: outletLogo, initials: outletInitials }}
        validUntil={validUntil}
        // Phase 2B · QR value is ONLY populated after reveal. Before
        // reveal we deliberately pass an empty string so the QR
        // element renders its "Tap Reveal" placeholder.
        qrValue={revealed ? (qrPayload || '') : ''}
        revealed={revealed}
        revealedCode={revealedCode}
        mode="read"
        size={size}
      />
    </VoucherErrorBoundary>
  );
}

export { designHealthy };
